<template>
  <div class="transferencias-container">

    <!-- ================= HEADER ================= -->
    <div class="page-header">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="deep-orange-darken-2" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-swap-horizontal</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Transferência de Estoque</h1>
              <div class="text-caption text-grey">
                Gerenciar transferências entre depósitos
              </div>
            </div>
          </div>
          
          <v-spacer />
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
            <v-btn
              color="indigo-darken-2"
              variant="tonal"
              @click="abrirPainelSapDownloads"
              prepend-icon="mdi-download-box"
              class="mr-2"
            >
              Painel Downloads
            </v-btn>
            
            <v-btn
              color="blue-darken-2"
              variant="tonal"
              @click="abrirDialogArquivosExcel"
              prepend-icon="mdi-file-excel"
              class="mr-2"
            >
              Arquivos Excel
            </v-btn>
            
            <v-btn
              v-if="transferenciasSelecionadas.length > 0"
              color="success"
              variant="flat"
              @click="validarEGerarExcel"
              prepend-icon="mdi-microsoft-excel"
              class="mr-2"
              :loading="loadingExcel"
              :disabled="!podeGerarExcel"
            >
              Gerar Excel ({{ transferenciasSelecionadas.length }})
            </v-btn>
            
            <v-btn
              color="deep-orange-darken-2"
              variant="flat"
              @click="abrirDialogNovo"
              prepend-icon="mdi-plus"
            >
              Nova Transferência
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- ================= FILTROS EXPANSÍVEIS ================= -->
    <v-card class="filters-card mb-4" elevation="2">
      <v-card-title class="filters-header pa-4">
        <div class="d-flex align-center w-100">
          <!-- Seção Esquerda - Título e Info -->
          <div class="d-flex align-center cursor-pointer flex-grow-1" @click="toggleFilters">
            <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
            <span class="filters-title">Filtros</span>
            
            <div class="d-flex align-center gap-2 ml-4">
              <v-chip variant="tonal" size="small">
                {{ pagination.total }} transferências
              </v-chip>
              
              <v-chip 
                v-if="hasActiveFilters"
                color="warning" 
                variant="tonal" 
                size="small"
              >
                {{ activeFiltersCount }} filtro(s)
              </v-chip>
            </div>
          </div>
          
          <!-- Seção Central - Botões de Ação -->
          <div class="d-flex align-center gap-2 mr-2">
            <v-btn
              color="white"
              variant="flat"
              size="small"
              @click="fetchData"
              :loading="loading"
            >
              <v-icon size="18" color="grey-darken-2">mdi-refresh</v-icon>
              <span class="ml-2 text-grey-darken-2">Atualizar</span>
            </v-btn>
            
            <v-btn
              class="ml-5"
              color="white"
              variant="flat"
              size="small"
              @click="limparFiltros"
              :disabled="!hasActiveFilters"
            >
              <v-icon size="18" :color="hasActiveFilters ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
              <span class="ml-2" :class="hasActiveFilters ? 'text-grey-darken-2' : 'text-grey-lighten-1'">Limpar</span>
            </v-btn>
          </div>
          
          <!-- Botão Expandir/Recolher -->
          <v-btn
            icon
            variant="text"
            size="small"
            color="white"
            @click="toggleFilters"
          >
            <v-icon size="20" :class="{ 'rotate-180': filtersExpanded }">
              mdi-chevron-down
            </v-icon>
            <v-tooltip activator="parent">
              {{ filtersExpanded ? 'Recolher Filtros' : 'Expandir Filtros' }}
            </v-tooltip>
          </v-btn>
        </div>
      </v-card-title>
      
      <!-- Conteúdo Expansível -->
      <v-expand-transition>
        <div v-show="filtersExpanded">
          <v-divider />
          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filtros.status"
                  :items="statusOptions"
                  label="Status"
                  placeholder="Todos os status"
                  prepend-inner-icon="mdi-flag"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="filtros.id_deposito_origem"
                  :items="depositos"
                  item-title="nome_deposito"
                  item-value="id_deposito"
                  label="Depósito Origem"
                  placeholder="Selecione..."
                  prepend-inner-icon="mdi-warehouse"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="filtros.id_deposito_destino"
                  :items="depositos"
                  item-title="nome_deposito"
                  item-value="id_deposito"
                  label="Depósito Destino"
                  placeholder="Selecione..."
                  prepend-inner-icon="mdi-warehouse"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="filtros.arquivo_gerado"
                  :items="[
                    { title: 'Todos', value: null },
                    { title: 'Não Gerados', value: 'nao' },
                    { title: 'Gerados', value: 'sim' }
                  ]"
                  label="Arquivo Excel"
                  placeholder="Todos"
                  prepend-inner-icon="mdi-file-excel"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.data_inicio"
                  label="Data Início"
                  type="date"
                  prepend-inner-icon="mdi-calendar"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.data_fim"
                  label="Data Fim"
                  type="date"
                  prepend-inner-icon="mdi-calendar"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>

              <v-col cols="12" md="6"></v-col>

              <v-col cols="12" md="3" class="d-flex justify-end align-center">
                <v-btn
                  color="deep-orange-darken-2"
                  variant="flat"
                  @click="fetchData"
                  :loading="loading"
                >
                  <v-icon start>mdi-filter</v-icon>
                  Filtrar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- ================= GRID / TABELA ================= -->
    <v-card class="table-card" elevation="2">
      <v-data-table-server
        v-model="transferenciasSelecionadas"
        :headers="headers"
        :items="transferencias"
        :items-length="pagination.total"
        :loading="loading"
        :item-selectable="itemSelecionavel"
        item-key="id"
        class="scrollable-table"
        density="compact"
        fixed-header
        height="calc(100vh - 420px)"
        show-select
        return-object
      >
        <!-- ID -->
        <template #item.id="{ item }">
          <v-chip
            color="grey-darken-1"
            variant="tonal"
            size="small"
          >
            #{{ item.id }}
          </v-chip>
        </template>

        <!-- Item Código -->
        <template #item.item_codigo="{ item }">
          <v-chip
            color="blue-grey"
            variant="tonal"
            size="small"
          >
            {{ item.item_codigo }}
          </v-chip>
        </template>

        <!-- Descrição -->
        <template #item.item_descricao="{ item }">
          <span class="text-caption">{{ item.item_descricao }}</span>
        </template>

        <!-- Quantidade -->
        <template #item.quantidade="{ item }">
          <span class="font-weight-bold">{{ formatarNumero(item.quantidade) }}</span>
        </template>

        <!-- Depósito Origem -->
        <template #item.deposito_origem="{ item }">
          <v-chip
            color="blue"
            variant="tonal"
            size="small"
          >
            {{ item.deposito_origem || 'N/A' }} - {{ getNomeDeposito(item.deposito_origem) }}
          </v-chip>
        </template>

        <!-- Depósito Destino -->
        <template #item.deposito_destino="{ item }">
          <v-chip
            color="teal"
            variant="tonal"
            size="small"
          >
            {{ item.deposito_destino || 'N/A' }} - {{ getNomeDeposito(item.deposito_destino) }}
          </v-chip>
        </template>

        <!-- Status -->
        <template #item.status_transferencia="{ item }">
          <v-chip
            :color="getStatusColor(item.status_transferencia)"
            variant="tonal"
            size="small"
          >
            {{ item.status_transferencia }}
          </v-chip>
        </template>

        <!-- Data de Geração do Arquivo -->
        <template #item.data_hora_geracao_arquivo="{ item }">
          <span v-if="item.data_hora_geracao_arquivo" class="text-caption">
            {{ formatarDataHora(item.data_hora_geracao_arquivo) }}
          </span>
          <span v-else class="text-caption text-grey">-</span>
        </template>

        <!-- Arquivo Gerado -->
        <template #item.arquivo_exportado="{ item }">
          <div v-if="item.arquivo_exportado">
            <v-chip color="success" variant="tonal" size="small">
              <v-icon start size="small">mdi-file-excel</v-icon>
              {{ item.arquivo_exportado }}
            </v-chip>
          </div>
          <span v-else class="text-caption text-grey">Não gerado</span>
        </template>

        <!-- Usuário -->
        <template #item.usuario="{ item }">
          <span class="text-caption">{{ item.usuario || 'N/A' }}</span>
        </template>

        <!-- Ações -->
        <template #item.acoes="{ item }">
          <div class="d-flex gap-1">
            <v-btn
              icon
              size="x-small"
              variant="text"
              color="blue"
              @click="visualizarTransferencia(item)"
            >
              <v-icon size="18">mdi-eye</v-icon>
              <v-tooltip activator="parent">Visualizar</v-tooltip>
            </v-btn>

            <v-btn
              v-if="item.status_transferencia === 'PENDENTE'"
              icon
              size="x-small"
              variant="text"
              color="orange"
              @click="editarTransferencia(item)"
              :disabled="!!item.arquivo_exportado"
            >
              <v-icon size="18">mdi-pencil</v-icon>
              <v-tooltip activator="parent">
                {{ item.arquivo_exportado ? 'Bloqueado - Faz parte de arquivo Excel' : 'Editar' }}
              </v-tooltip>
            </v-btn>

            <v-btn
              v-if="item.status_transferencia === 'ERRO'"
              icon
              size="x-small"
              variant="text"
              color="error"
              @click="verErro(item)"
            >
              <v-icon size="18">mdi-alert-circle</v-icon>
              <v-tooltip activator="parent">Ver Erro</v-tooltip>
            </v-btn>

            <v-btn
              v-if="item.status_transferencia === 'PENDENTE'"
              icon
              size="x-small"
              variant="text"
              color="error"
              @click="excluirTransferencia(item)"
              :disabled="!!item.arquivo_exportado"
            >
              <v-icon size="18">mdi-delete</v-icon>
              <v-tooltip activator="parent">
                {{ item.arquivo_exportado ? 'Bloqueado - Faz parte de arquivo Excel' : 'Excluir' }}
              </v-tooltip>
            </v-btn>

            <!-- Indicador de arquivo Excel -->
            <v-icon 
              v-if="item.arquivo_exportado" 
              size="18" 
              color="orange"
              class="ml-1"
            >
              mdi-file-excel-box
              <v-tooltip activator="parent">
                Faz parte do arquivo: {{ item.arquivo_exportado }}
              </v-tooltip>
            </v-icon>
          </div>
        </template>

        <!-- Nenhum resultado -->
        <template #no-data>
          <div class="text-center pa-6 text-grey">
            Nenhuma transferência encontrada. Ajuste os filtros e clique em "Filtrar".
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <!-- ================= DIALOG NOVA/EDITAR TRANSFERÊNCIA ================= -->
    <v-dialog v-model="dialogForm" max-width="900" scrollable persistent>
      <v-card>
        <v-card-title class="bg-deep-orange-darken-2 text-white d-flex align-center">
          <v-icon class="mr-2">{{ modoEdicao ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ modoEdicao ? 'Editar Transferência' : 'Nova Transferência' }}
        </v-card-title>
        
        <v-card-text class="pa-4">
          <v-form ref="formTransferencia">
            <v-row dense>
              <!-- Código do Produto com botão de lupa -->
              <v-col cols="12" md="10">
                <v-text-field
                  v-model="form.item_codigo"
                  label="Código do Produto *"
                  prepend-inner-icon="mdi-barcode"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Campo obrigatório']"
                  @blur="buscarProdutoPorCodigo"
                  @keyup.enter="buscarProdutoPorCodigo"
                  :loading="loadingProdutos"
                  placeholder="Digite o código do produto"
                  hint="Digite o código e pressione Enter ou clique na lupa para buscar"
                  persistent-hint
                />
              </v-col>

              <!-- Botão Lupa para abrir dialog de consulta -->
              <v-col cols="12" md="2">
                <v-btn
                  color="deep-orange-darken-2"
                  variant="flat"
                  block
                  @click="abrirDialogConsultaProdutos"
                  height="40"
                >
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </v-col>

              <!-- Informações do Produto Selecionado -->
              <v-col cols="12" v-if="produtoSelecionado">
                <v-card variant="tonal" color="blue-grey-lighten-4">
                  <v-card-text class="pa-3">
                    <v-row dense>
                      <!-- Primeira linha: Código, Nome e Tamanho -->
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey-darken-1">Código</div>
                        <div class="font-weight-bold">{{ produtoSelecionado.codigo_produto }}</div>
                      </v-col>
                      <v-col cols="12" md="6">
                        <div class="text-caption text-grey-darken-1">Nome do Produto</div>
                        <div class="font-weight-bold text-truncate" :title="produtoSelecionado.nome_produto">{{ produtoSelecionado.nome_produto }}</div>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey-darken-1">Tamanho</div>
                        <div class="font-weight-bold">{{ produtoSelecionado.tamanho || 'N/A' }}</div>
                      </v-col>
                      
                      <!-- Segunda linha: Grupo e Estoques -->
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey-darken-1">Grupo</div>
                        <div class="font-weight-bold">{{ produtoSelecionado.nome_grupo_produto || 'N/A' }}</div>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey-darken-1">Estoque Físico</div>
                        <div class="font-weight-bold text-success">{{ produtoSelecionado.estoque_fisico || 0 }}</div>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey-darken-1">Comprometido</div>
                        <div class="font-weight-bold text-warning">{{ produtoSelecionado.comprometido || 0 }}</div>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey-darken-1">Disponível</div>
                        <div class="font-weight-bold text-primary">{{ produtoSelecionado.disponivel_previsto || 0 }}</div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Quantidade e Unidade de Medida -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.quantidade"
                  type="number"
                  label="Quantidade *"
                  prepend-inner-icon="mdi-counter"
                  variant="outlined"
                  density="compact"
                  :rules="[
                    v => !!v || 'Campo obrigatório', 
                    v => v > 0 || 'Deve ser maior que zero',
                    v => !produtoSelecionado || v <= produtoSelecionado.disponivel_previsto || `Quantidade não pode ser maior que o disponível (${produtoSelecionado.disponivel_previsto})`
                  ]"
                  :max="produtoSelecionado?.disponivel_previsto || 999999"
                  min="0.01"
                  step="0.01"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.unidade_medida"
                  label="Unidade de Medida *"
                  prepend-inner-icon="mdi-ruler"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Campo obrigatório']"
                  readonly
                  hint="Preenchido automaticamente ao selecionar o produto"
                  persistent-hint
                />
              </v-col>


              <!-- Depósito Origem (filtrado por estoque do produto) -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.deposito_origem"
                  :items="depositosComEstoque"
                  item-title="nome_deposito"
                  item-value="id_deposito"
                  label="Depósito Origem *"
                  prepend-inner-icon="mdi-warehouse"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Campo obrigatório']"
                  :disabled="!produtoSelecionado"
                  @update:model-value="onDepositoOrigemChange"
                  :hint="!produtoSelecionado ? 'Selecione um produto primeiro' : `${depositosComEstoque.length} depósito(s) com estoque`"
                  persistent-hint
                />
              </v-col>

              <!-- Posição Origem -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.posicao_origem"
                  :items="enderecosOrigem"
                  item-title="localizacao"
                  item-value="localizacao"
                  :label="enderecosOrigem.length > 0 ? 'Posição Origem *' : 'Posição Origem (Opcional)'"
                  prepend-inner-icon="mdi-map-marker"
                  variant="outlined"
                  density="compact"
                  :rules="enderecosOrigem.length > 0 ? [v => !!v || 'Campo obrigatório'] : []"
                  :disabled="!form.deposito_origem"
                  clearable
                  :no-data-text="!form.deposito_origem ? 'Selecione um depósito primeiro' : 'Depósito sem endereços cadastrados'"
                  :hint="enderecosOrigem.length === 0 && form.deposito_origem ? 'Este depósito não possui endereços cadastrados' : ''"
                  persistent-hint
                >
                  <template #item="{ props, item }">
                    <v-list-item 
                      v-bind="props"
                      :title="item.raw?.localizacao || ''"
                      :subtitle="item.raw?.endereco && item.raw?.prateleira ? `${item.raw.endereco} - ${item.raw.prateleira}` : ''"
                    >
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Depósito Destino -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.deposito_destino"
                  :items="depositos"
                  item-title="nome_deposito"
                  item-value="id_deposito"
                  label="Depósito Destino *"
                  prepend-inner-icon="mdi-warehouse"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Campo obrigatório', v => v !== form.deposito_origem || 'Deve ser diferente da origem']"
                  @update:model-value="onDepositoDestinoChange"
                />
              </v-col>

              <!-- Posição Destino -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.posicao_destino"
                  :items="enderecosDestino"
                  item-title="localizacao"
                  item-value="localizacao"
                  :label="enderecosDestino.length > 0 ? 'Posição Destino *' : 'Posição Destino (Opcional)'"
                  prepend-inner-icon="mdi-map-marker"
                  variant="outlined"
                  density="compact"
                  :rules="enderecosDestino.length > 0 ? [v => !!v || 'Campo obrigatório'] : []"
                  :disabled="!form.deposito_destino"
                  clearable
                  :no-data-text="!form.deposito_destino ? 'Selecione um depósito primeiro' : 'Depósito sem endereços cadastrados'"
                  :hint="enderecosDestino.length === 0 && form.deposito_destino ? 'Este depósito não possui endereços cadastrados' : ''"
                  persistent-hint
                >
                  <template #item="{ props, item }">
                    <v-list-item 
                      v-bind="props"
                      :title="item.raw?.localizacao || ''"
                      :subtitle="item.raw?.endereco && item.raw?.prateleira ? `${item.raw.endereco} - ${item.raw.prateleira}` : ''"
                    >
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Observação -->
              <v-col cols="12">
                <v-textarea
                  v-model="form.observacao"
                  label="Observação"
                  prepend-inner-icon="mdi-text"
                  variant="outlined"
                  density="compact"
                  rows="3"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="fecharDialogForm"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="deep-orange-darken-2"
            variant="flat"
            @click="salvarTransferencia"
            :loading="salvando"
          >
            {{ modoEdicao ? 'Atualizar' : 'Salvar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG VISUALIZAR ================= -->
    <v-dialog v-model="dialogVisualizar" max-width="800" scrollable>
      <v-card>
        <v-card-title class="bg-blue text-white d-flex align-center">
          <v-icon class="mr-2">mdi-eye</v-icon>
          Detalhes da Transferência #{{ transferenciaSelecionada?.id }}
        </v-card-title>
        
        <v-card-text class="pa-4">
          <v-row v-if="transferenciaSelecionada" dense>
            <v-col cols="12" md="6">
              <strong>Status:</strong>
              <v-chip
                :color="getStatusColor(transferenciaSelecionada.status)"
                variant="tonal"
                size="small"
                class="ml-2"
              >
                {{ transferenciaSelecionada.status }}
              </v-chip>
            </v-col>

            <v-col cols="12" md="6">
              <strong>Data Solicitação:</strong> {{ formatarData(transferenciaSelecionada.data_solicitacao) }}
            </v-col>

            <v-col cols="12" md="6">
              <strong>Depósito Origem:</strong> {{ transferenciaSelecionada.deposito_origem?.nome_deposito }}
            </v-col>

            <v-col cols="12" md="6">
              <strong>Depósito Destino:</strong> {{ transferenciaSelecionada.deposito_destino?.nome_deposito }}
            </v-col>

            <v-col cols="12" md="6">
              <strong>Solicitante:</strong> {{ transferenciaSelecionada.usuario_solicitante?.name }}
            </v-col>

            <v-col cols="12" md="6" v-if="transferenciaSelecionada.usuario_aprovador">
              <strong>Aprovador:</strong> {{ transferenciaSelecionada.usuario_aprovador?.name }}
            </v-col>

            <v-col cols="12" v-if="transferenciaSelecionada.observacao">
              <strong>Observação:</strong>
              <p class="mt-1">{{ transferenciaSelecionada.observacao }}</p>
            </v-col>

            <v-col cols="12">
              <v-divider class="my-2" />
              <h3 class="text-subtitle-1 mb-2">Itens</h3>
              <v-data-table
                :headers="headersItensVisualizar"
                :items="transferenciaSelecionada.itens || []"
                density="compact"
                :items-per-page="-1"
                hide-default-footer
              />
            </v-col>
          </v-row>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="dialogVisualizar = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG HISTÓRICO ================= -->
    <v-dialog v-model="dialogHistorico" max-width="900" scrollable>
      <v-card>
        <v-card-title class="bg-purple text-white d-flex align-center">
          <v-icon class="mr-2">mdi-history</v-icon>
          Histórico da Transferência #{{ transferenciaSelecionada?.id }}
        </v-card-title>
        
        <v-card-text class="pa-0">
          <v-data-table
            :headers="headersHistorico"
            :items="historico"
            :loading="loadingHistorico"
            density="compact"
            :items-per-page="-1"
            hide-default-footer
          >
            <template #item.data_hora="{ item }">
              <span class="text-caption">{{ formatarDataHora(item.data_hora) }}</span>
            </template>

            <template #item.status_anterior="{ item }">
              <v-chip
                v-if="item.status_anterior"
                :color="getStatusColor(item.status_anterior)"
                variant="tonal"
                size="x-small"
              >
                {{ item.status_anterior }}
              </v-chip>
              <span v-else>-</span>
            </template>

            <template #item.status_novo="{ item }">
              <v-chip
                :color="getStatusColor(item.status_novo)"
                variant="tonal"
                size="x-small"
              >
                {{ item.status_novo }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="dialogHistorico = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG CONFIRMAÇÃO ================= -->
    <v-dialog v-model="dialogConfirmacao" max-width="500">
      <v-card>
        <v-card-title class="bg-warning text-white d-flex align-center">
          <v-icon class="mr-2">mdi-alert</v-icon>
          {{ tituloConfirmacao }}
        </v-card-title>
        
        <v-card-text class="pa-4">
          <p>{{ mensagemConfirmacao }}</p>
          
          <v-textarea
            v-if="requererMotivo"
            v-model="motivoAcao"
            label="Motivo *"
            variant="outlined"
            density="compact"
            rows="3"
            class="mt-3"
            :rules="[v => !!v || 'Motivo é obrigatório']"
          />

          <v-textarea
            v-if="requererObservacao"
            v-model="observacaoAcao"
            label="Observação"
            variant="outlined"
            density="compact"
            rows="3"
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" variant="text" @click="dialogConfirmacao = false">
            Cancelar
          </v-btn>
          <v-btn :color="corBotaoConfirmacao" variant="flat" @click="executarAcao">
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Consulta de Produtos -->
    <v-dialog v-model="dialogConsultaProdutos" max-width="1200" scrollable>
      <v-card>
        <v-card-title class="bg-deep-orange-darken-2 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-package-variant</v-icon>
          Consultar Produtos
          <v-spacer />
          <v-btn icon variant="text" @click="dialogConsultaProdutos = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Filtros de busca -->
          <v-row dense class="mb-4">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="consultaProdutos.filtros.codigo"
                label="Código do Produto"
                prepend-inner-icon="mdi-barcode"
                variant="outlined"
                density="compact"
                clearable
                @keyup.enter="buscarProdutosDialog"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="consultaProdutos.filtros.nome"
                label="Nome do Produto"
                prepend-inner-icon="mdi-text"
                variant="outlined"
                density="compact"
                clearable
                @keyup.enter="buscarProdutosDialog"
              />
            </v-col>
            <v-col cols="12">
              <v-btn
                color="deep-orange-darken-2"
                variant="flat"
                @click="buscarProdutosDialog"
                :loading="consultaProdutos.loading"
              >
                <v-icon start>mdi-magnify</v-icon>
                Buscar
              </v-btn>
            </v-col>
          </v-row>

          <!-- Tabela de produtos -->
          <v-data-table
            :headers="consultaProdutos.headers"
            :items="consultaProdutos.produtos"
            :loading="consultaProdutos.loading"
            density="compact"
            :items-per-page="10"
            class="elevation-1"
          >
            <template #item.codigo_produto="{ item }">
              <v-chip color="blue-darken-1" variant="tonal" size="small">
                {{ item.codigo_produto }}
              </v-chip>
            </template>

            <template #item.estoque_fisico="{ item }">
              <span :class="item.estoque_fisico > 0 ? 'text-success font-weight-bold' : 'text-grey'">
                {{ item.estoque_fisico || 0 }}
              </span>
            </template>

            <template #item.disponivel_previsto="{ item }">
              <span :class="item.disponivel_previsto > 0 ? 'text-primary font-weight-bold' : 'text-grey'">
                {{ item.disponivel_previsto || 0 }}
              </span>
            </template>

            <template #item.acoes="{ item }">
              <v-btn
                color="deep-orange-darken-2"
                variant="tonal"
                size="small"
                @click="selecionarProdutoDialog(item)"
              >
                <v-icon start size="small">mdi-check</v-icon>
                Selecionar
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Arquivos Excel Gerados -->
    <v-dialog v-model="dialogArquivosExcel" max-width="900" scrollable>
      <v-card>
        <v-card-title class="bg-blue-darken-2 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-file-excel</v-icon>
          Arquivos Excel Gerados
          <v-spacer />
          <v-btn icon variant="text" @click="dialogArquivosExcel = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-data-table
            :headers="[
              { title: 'Arquivo', key: 'arquivo_exportado' },
              { title: 'Data Geração', key: 'data_hora_geracao_arquivo', width: '180px' },
              { title: 'Qtd. Transferências', key: 'quantidade_transferencias', width: '150px' },
              { title: 'Ações', key: 'acoes', width: '200px', sortable: false }
            ]"
            :items="arquivosExcel"
            :loading="loadingArquivosExcel"
            density="compact"
            :items-per-page="10"
            class="elevation-1"
          >
            <template #item.arquivo_exportado="{ item }">
              <div class="d-flex align-center gap-2">
                <v-chip color="green-darken-1" variant="tonal" size="small">
                  <v-icon start size="small">mdi-file-excel</v-icon>
                  {{ item.arquivo_exportado }}
                </v-chip>
                <v-chip 
                  v-if="arquivoRecemGerado(item)"
                  color="blue" 
                  variant="tonal" 
                  size="x-small"
                >
                  <v-icon start size="x-small">mdi-check-circle</v-icon>
                  Baixado
                </v-chip>
              </div>
            </template>

            <template #item.data_hora_geracao_arquivo="{ item }">
              <span class="text-caption">{{ formatarDataHora(item.data_hora_geracao_arquivo) }}</span>
            </template>

            <template #item.quantidade_transferencias="{ item }">
              <v-chip color="blue" variant="tonal" size="small">
                {{ item.quantidade_transferencias }}
              </v-chip>
            </template>

            <template #item.acoes="{ item }">
              <div class="d-flex gap-1">
                <v-tooltip>
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      color="success"
                      variant="tonal"
                      size="small"
                      @click="baixarArquivoExcel(item.arquivo_exportado)"
                    >
                      <v-icon start size="small">mdi-download</v-icon>
                      Baixar
                    </v-btn>
                  </template>
                  <span v-if="arquivoRecemGerado(item)">
                    Arquivo já foi baixado automaticamente na geração, mas você pode baixar novamente
                  </span>
                  <span v-else>
                    Baixar arquivo Excel
                  </span>
                </v-tooltip>
                <v-btn
                  color="error"
                  variant="tonal"
                  size="small"
                  @click="confirmarCancelarArquivo(item)"
                >
                  <v-icon start size="small">mdi-cancel</v-icon>
                  Cancelar
                </v-btn>
              </div>
            </template>

            <template #no-data>
              <div class="text-center pa-6 text-grey">
                Nenhum arquivo Excel gerado ainda.
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="dialogArquivosExcel = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { transferenciaEstoqueService } from "@/services/transferenciaEstoqueService";
import { sapeJodarcService } from "@/services/sapeJodarcService";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "TransferenciaDeEstoque",

  data() {
    return {
      loading: false,
      salvando: false,
      executandoAcao: false,
      loadingHistorico: false,
      loadingProdutos: false,
      loadingDepositos: false,
      searchProduto: '',
      searchTimeout: null,
      filtersExpanded: true,
      
      transferencias: [],
      transferenciasSelecionadas: [],
      loadingExcel: false,
      linkDownloadExcel: null,
      arquivosExcel: [],
      loadingArquivosExcel: false,
      arquivosRecemGerados: [], // Lista de arquivos gerados nos últimos 5 minutos
      depositos: [],
      produtos: [],
      produtoSelecionado: null,
      estoquePorDeposito: [],
      enderecosOrigem: [],
      enderecosDestino: [],
      historico: [],
      
      dialogForm: false,
      dialogVisualizar: false,
      dialogHistorico: false,
      dialogConfirmacao: false,
      dialogConsultaProdutos: false,
      dialogArquivosExcel: false,
      
      modoEdicao: false,
      transferenciaSelecionada: null,
      
      consultaProdutos: {
        loading: false,
        produtos: [],
        filtros: {
          codigo: '',
          nome: '',
        },
        headers: [
          { title: "Código", key: "codigo_produto", width: "120px" },
          { title: "Nome", key: "nome_produto" },
          { title: "Grupo", key: "nome_grupo_produto", width: "150px" },
          { title: "Estoque", key: "estoque_fisico", width: "100px" },
          { title: "Disponível", key: "disponivel_previsto", width: "100px" },
          { title: "Ações", key: "acoes", width: "150px", sortable: false },
        ],
      },
      
      tituloConfirmacao: "",
      mensagemConfirmacao: "",
      corBotaoConfirmacao: "primary",
      requererMotivo: false,
      requererObservacao: false,
      motivoAcao: "",
      observacaoAcao: "",
      acaoCallback: null,

      filtros: {
        status: null,
        id_deposito_origem: null,
        id_deposito_destino: null,
        data_inicio: null,
        data_fim: null,
        arquivo_gerado: null, // null = todos, 'sim' = gerados, 'nao' = não gerados
      },

      form: {
        item_codigo: null,
        item_descricao: "",
        quantidade: null,
        deposito_origem: null,
        posicao_origem: null,
        deposito_destino: null,
        posicao_destino: null,
        saldo_estoque: null,
        unidade_medida: "",
        unidade_medida_estoque: false,
        observacao: "",
      },

      pagination: {
        total: 0,
      },

      statusOptions: [
        { title: "Pendente", value: "PENDENTE" },
        { title: "Processando", value: "PROCESSANDO" },
        { title: "Concluído", value: "CONCLUIDO" },
        { title: "Erro", value: "ERRO" },
        { title: "Cancelado", value: "CANCELADO" },
      ],

      headers: [
        { title: "ID", key: "id", width: "80px" },
        { title: "Item", key: "item_codigo", width: "120px" },
        { title: "Descrição", key: "item_descricao" },
        { title: "Qtd", key: "quantidade", width: "100px" },
        { title: "Origem", key: "deposito_origem", width: "180px" },
        { title: "Destino", key: "deposito_destino", width: "180px" },
        { title: "Status", key: "status_transferencia", width: "120px" },
        { title: "Data Geração", key: "data_hora_geracao_arquivo", width: "150px" },
        { title: "Arquivo Gerado", key: "arquivo_exportado", width: "200px" },
        { title: "Usuário", key: "usuario", width: "150px" },
        { title: "Ações", key: "acoes", width: "200px", sortable: false },
      ],

      headersItens: [
        { title: "Código Produto", key: "codigo_produto" },
        { title: "Quantidade", key: "quantidade", width: "150px" },
        { title: "Ações", key: "acoes", width: "80px", sortable: false },
      ],

      headersItensVisualizar: [
        { title: "Código Produto", key: "codigo_produto" },
        { title: "Quantidade", key: "quantidade" },
      ],

      headersHistorico: [
        { title: "Data/Hora", key: "data_hora", width: "180px" },
        { title: "Status Anterior", key: "status_anterior", width: "130px" },
        { title: "Status Novo", key: "status_novo", width: "130px" },
        { title: "Usuário", key: "usuario" },
        { title: "Observação", key: "observacao" },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filtros.status ||
        this.filtros.id_deposito_origem ||
        this.filtros.id_deposito_destino ||
        this.filtros.data_inicio ||
        this.filtros.data_fim
      );
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filtros.status) count++;
      if (this.filtros.id_deposito_origem) count++;
      if (this.filtros.id_deposito_destino) count++;
      if (this.filtros.data_inicio) count++;
      if (this.filtros.data_fim) count++;
      return count;
    },
    // Filtrar depósitos que tenham estoque do produto selecionado
    depositosComEstoque() {
      if (!this.produtoSelecionado || this.estoquePorDeposito.length === 0) {
        return [];
      }
      
      // Filtrar depósitos que tenham estoque disponível do produto
      const depositosComSaldo = this.estoquePorDeposito
        .filter(estoque => estoque.estoque_disponivel > 0)
        .map(estoque => estoque.codigo_deposito);
      
      console.log('📦 Depósitos com saldo:', depositosComSaldo);
      
      // Retornar apenas depósitos que estão na lista de depósitos e têm saldo
      return this.depositos.filter(dep => depositosComSaldo.includes(dep.id_deposito));
    },

    // Verificar se pode gerar Excel (nenhuma transferência selecionada deve ter arquivo gerado)
    podeGerarExcel() {
      if (this.transferenciasSelecionadas.length === 0) {
        return false;
      }
      
      // Verificar se alguma transferência já tem arquivo gerado
      const temArquivoGerado = this.transferenciasSelecionadas.some(
        t => t.arquivo_exportado && t.arquivo_exportado.trim() !== ''
      );
      
      return !temArquivoGerado;
    },
  },

  mounted() {
    this.fetchData();
    this.fetchDepositos();
    // Não carrega produtos no início - só quando o usuário buscar
  },

  methods: {
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    async fetchData() {
      this.loading = true;
      try {
        const response = await transferenciaEstoqueService.listarTransferencias(this.filtros);

        if (response?.success) {
          this.transferencias = Array.isArray(response.dados) ? response.dados : [];
          this.pagination.total = response.total || 0;
        } else {
          this.transferencias = [];
          toast.error(response?.message || "Erro ao carregar transferências");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar transferências");
        this.transferencias = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchDepositos() {
      this.loadingDepositos = true;
      try {
        const response = await sapeJodarcService.sapeDeposito({});
        if (response?.success) {
          this.depositos = Array.isArray(response.data) ? response.data : [];
          console.log("✅ Depósitos SAP carregados:", this.depositos.length);
        } else {
          this.depositos = [];
          toast.error(response?.message || "Erro ao carregar depósitos");
        }
      } catch (error) {
        console.error("Erro ao carregar depósitos:", error);
        toast.error("Erro ao carregar depósitos do SAP");
        this.depositos = [];
      } finally {
        this.loadingDepositos = false;
      }
    },

    // Buscar produto ao sair do campo ou pressionar Enter
    async buscarProdutoPorCodigo() {
      if (!this.form.item_codigo || this.form.item_codigo.length < 2) {
        return;
      }
      
      this.loadingProdutos = true;
      try {
        const response = await sapeJodarcService.sapeProdutos({
          page: 1,
          per_page: 1,
          codigo_produto: this.form.item_codigo,
        });
        
        if (response?.success && response.data.length > 0) {
          const produto = response.data[0];
          await this.selecionarProduto(produto);
        } else {
          toast.warning("Produto não encontrado. Clique na lupa para buscar.");
          this.produtoSelecionado = null;
        }
      } catch (error) {
        console.error("Erro ao buscar produto:", error);
        toast.error("Erro ao buscar produto");
      } finally {
        this.loadingProdutos = false;
      }
    },

    // Abrir dialog de consulta de produtos
    abrirDialogConsultaProdutos() {
      this.consultaProdutos.filtros.codigo = '';
      this.consultaProdutos.filtros.nome = '';
      this.consultaProdutos.produtos = [];
      this.dialogConsultaProdutos = true;
    },

    // Buscar produtos no dialog
    async buscarProdutosDialog() {
      if (!this.consultaProdutos.filtros.codigo && !this.consultaProdutos.filtros.nome) {
        toast.warning("Digite o código ou nome do produto");
        return;
      }

      this.consultaProdutos.loading = true;
      try {
        const params = {
          page: 1,
          per_page: 50,
        };

        if (this.consultaProdutos.filtros.codigo) {
          params.codigo_produto = this.consultaProdutos.filtros.codigo;
        }
        if (this.consultaProdutos.filtros.nome) {
          params.nome_produto = this.consultaProdutos.filtros.nome;
        }

        const response = await sapeJodarcService.sapeProdutos(params);
        
        if (response?.success) {
          this.consultaProdutos.produtos = Array.isArray(response.data) ? response.data : [];
          
          if (this.consultaProdutos.produtos.length === 0) {
            toast.info("Nenhum produto encontrado");
          }
        } else {
          this.consultaProdutos.produtos = [];
          toast.error("Erro ao buscar produtos");
        }
      } catch (error) {
        console.error("Erro ao buscar produtos:", error);
        this.consultaProdutos.produtos = [];
        toast.error("Erro ao buscar produtos");
      } finally {
        this.consultaProdutos.loading = false;
      }
    },

    // Selecionar produto do dialog
    async selecionarProdutoDialog(produto) {
      await this.selecionarProduto(produto);
      this.dialogConsultaProdutos = false;
      toast.success("Produto selecionado com sucesso");
    },

    // Método unificado para selecionar produto
    async selecionarProduto(produto) {
      this.produtoSelecionado = produto;
      this.form.item_codigo = produto.codigo_produto;
      this.form.item_descricao = produto.nome_produto;
      this.form.unidade_medida = produto.unidade_medida || 'UN';
      this.form.unidade_medida_estoque = true; // Sempre true
      
      console.log('✅ Produto selecionado:', produto);
      
      // Buscar estoque por depósito
      await this.fetchEstoquePorDeposito(produto.codigo_produto);
    },

    async fetchProdutos(searchTerm) {
      if (!searchTerm || searchTerm.length < 2) {
        return;
      }
      
      this.loadingProdutos = true;
      try {
        // Detectar se é código (letras+números) ou nome (texto)
        // Se tiver apenas letras e números sem espaços, busca por código
        // Caso contrário, busca por nome
        const isCodigoProduto = /^[A-Z0-9]+$/i.test(searchTerm);
        
        const params = {
          page: 1,
          per_page: 50,
        };
        
        if (isCodigoProduto) {
          params.codigo_produto = searchTerm;
          console.log("🔍 Buscando por código:", searchTerm);
        } else {
          params.nome_produto = searchTerm;
          console.log("🔍 Buscando por nome:", searchTerm);
        }
        
        const response = await sapeJodarcService.sapeProdutos(params);
        
        if (response?.success) {
          this.produtos = Array.isArray(response.data) ? response.data : [];
          console.log("✅ Produtos encontrados:", this.produtos.length);
          
          if (this.produtos.length === 0) {
            toast.info("Nenhum produto encontrado");
          }
        } else {
          this.produtos = [];
          toast.error("Erro ao buscar produtos");
        }
      } catch (error) {
        console.error("Erro ao buscar produtos:", error);
        this.produtos = [];
        toast.error("Erro ao buscar produtos");
      } finally {
        this.loadingProdutos = false;
      }
    },

    async fetchEnderecosOrigem(depositoCodigo) {
      if (!depositoCodigo) {
        this.enderecosOrigem = [];
        return;
      }
      
      // Buscar endereços do depósito selecionado
      const deposito = this.depositos.find(d => d.id_deposito === depositoCodigo);
      if (deposito && deposito.enderecos) {
        this.enderecosOrigem = deposito.enderecos;
        console.log("✅ Endereços origem carregados:", this.enderecosOrigem.length);
      } else {
        this.enderecosOrigem = [];
        console.log("⚠️ Depósito sem endereços cadastrados");
      }
    },

    async fetchEnderecosDestino(depositoCodigo) {
      if (!depositoCodigo) {
        this.enderecosDestino = [];
        return;
      }
      
      // Buscar endereços do depósito selecionado
      const deposito = this.depositos.find(d => d.id_deposito === depositoCodigo);
      if (deposito && deposito.enderecos) {
        this.enderecosDestino = deposito.enderecos;
        console.log("✅ Endereços destino carregados:", this.enderecosDestino.length);
      } else {
        this.enderecosDestino = [];
        console.log("⚠️ Depósito sem endereços cadastrados");
      }
    },

    onDepositoOrigemChange() {
      this.form.posicao_origem = null;
      this.enderecosOrigem = [];
      if (this.form.deposito_origem) {
        this.fetchEnderecosOrigem(this.form.deposito_origem);
      }
    },

    onDepositoDestinoChange() {
      this.form.posicao_destino = null;
      this.enderecosDestino = [];
      if (this.form.deposito_destino) {
        this.fetchEnderecosDestino(this.form.deposito_destino);
      }
    },


    async onProdutoChange(codigoProduto) {
      if (codigoProduto) {
        const produto = this.produtos.find(p => p.codigo_produto === codigoProduto);
        if (produto) {
          this.produtoSelecionado = produto;
          this.form.item_descricao = produto.nome_produto;
          this.form.unidade_medida = produto.unidade_medida || 'UN';
          console.log('✅ Produto selecionado:', produto);
          
          // Buscar estoque por depósito
          await this.fetchEstoquePorDeposito(codigoProduto);
        }
      } else {
        this.produtoSelecionado = null;
        this.estoquePorDeposito = [];
        this.form.deposito_origem = null;
        this.form.posicao_origem = null;
        this.enderecosOrigem = [];
      }
    },

    async fetchEstoquePorDeposito(codigoProduto) {
      try {
        const params = {
          codigo_produto: codigoProduto,
          per_page: 100,
        };
        const response = await sapeJodarcService.sapeProdutosEstoquesDepositos(params);
        
        if (response?.success) {
          this.estoquePorDeposito = Array.isArray(response.data) ? response.data : [];
          console.log('✅ Estoque por depósito carregado:', this.estoquePorDeposito.length);
        } else {
          this.estoquePorDeposito = [];
        }
      } catch (error) {
        console.error('❌ Erro ao carregar estoque por depósito:', error);
        this.estoquePorDeposito = [];
      }
    },

    limparFiltros() {
      this.filtros = {
        status: null,
        id_deposito_origem: null,
        id_deposito_destino: null,
        data_inicio: null,
        data_fim: null,
      };
      this.fetchData();
    },

    abrirDialogNovo() {
      this.modoEdicao = false;
      this.form = {
        item_codigo: null,
        item_descricao: "",
        quantidade: null,
        deposito_origem: null,
        posicao_origem: null,
        deposito_destino: null,
        posicao_destino: null,
        saldo_estoque: null,
        unidade_medida: "",
        unidade_medida_estoque: false,
        observacao: "",
      };
      this.enderecosOrigem = [];
      this.enderecosDestino = [];
      this.dialogForm = true;
    },

    editarTransferencia(item) {
      this.modoEdicao = true;
      this.transferenciaSelecionada = item;
      this.form = {
        item_codigo: item.item_codigo,
        item_descricao: item.item_descricao,
        quantidade: item.quantidade,
        deposito_origem: item.deposito_origem,
        posicao_origem: item.posicao_origem,
        deposito_destino: item.deposito_destino,
        posicao_destino: item.posicao_destino,
        saldo_estoque: item.saldo_estoque,
        unidade_medida_estoque: item.unidade_medida_estoque || false,
        unidade_medida: item.unidade_medida,
        observacao: item.observacao || "",
      };
      
      // Carregar endereços dos depósitos selecionados
      if (item.deposito_origem) {
        this.fetchEnderecosOrigem(item.deposito_origem);
      }
      if (item.deposito_destino) {
        this.fetchEnderecosDestino(item.deposito_destino);
      }
      
      this.dialogForm = true;
    },

    fecharDialogForm() {
      this.dialogForm = false;
      this.modoEdicao = false;
      this.transferenciaSelecionada = null;
    },

    async salvarTransferencia() {
      const { valid } = await this.$refs.formTransferencia.validate();
      if (!valid) {
        toast.warning("Preencha todos os campos obrigatórios");
        return;
      }

      this.salvando = true;
      try {
        const dados = {
          item_codigo: this.form.item_codigo,
          item_descricao: this.form.item_descricao,
          quantidade: this.form.quantidade,
          deposito_origem: this.form.deposito_origem,
          posicao_origem: this.form.posicao_origem,
          deposito_destino: this.form.deposito_destino,
          posicao_destino: this.form.posicao_destino,
          saldo_estoque: this.produtoSelecionado?.disponivel_previsto || 0,
          unidade_medida: this.form.unidade_medida,
          unidade_medida_estoque: this.form.unidade_medida_estoque,
          observacao: this.form.observacao,
        };

        let response;
        if (this.modoEdicao) {
          response = await transferenciaEstoqueService.atualizarTransferencia(
            this.transferenciaSelecionada.id,
            dados
          );
        } else {
          response = await transferenciaEstoqueService.criarTransferencia(dados);
        }

        if (response?.success) {
          toast.success(response.message || "Transferência salva com sucesso");
          this.fecharDialogForm();
          this.fetchData();
        } else {
          toast.error(response?.message || "Erro ao salvar transferência");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao salvar transferência");
      } finally {
        this.salvando = false;
      }
    },

    visualizarTransferencia(item) {
      this.transferenciaSelecionada = item;
      this.dialogVisualizar = true;
    },

    aprovarTransferencia(item) {
      this.transferenciaSelecionada = item;
      this.tituloConfirmacao = "Aprovar Transferência";
      this.mensagemConfirmacao = `Deseja aprovar a transferência #${item.id}?`;
      this.corBotaoConfirmacao = "success";
      this.requererMotivo = false;
      this.requererObservacao = true;
      this.observacaoAcao = "";
      this.acaoCallback = this.executarAprovacao;
      this.dialogConfirmacao = true;
    },

    async executarAprovacao() {
      this.executandoAcao = true;
      try {
        const response = await transferenciaEstoqueService.aprovarTransferencia(
          this.transferenciaSelecionada.id,
          { observacao_aprovacao: this.observacaoAcao }
        );

        if (response?.success) {
          toast.success(response.message || "Transferência aprovada com sucesso");
          this.dialogConfirmacao = false;
          this.fetchData();
        } else {
          toast.error(response?.message || "Erro ao aprovar transferência");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao aprovar transferência");
      } finally {
        this.executandoAcao = false;
      }
    },

    rejeitarTransferencia(item) {
      this.transferenciaSelecionada = item;
      this.tituloConfirmacao = "Rejeitar Transferência";
      this.mensagemConfirmacao = `Deseja rejeitar a transferência #${item.id}?`;
      this.corBotaoConfirmacao = "error";
      this.requererMotivo = true;
      this.requererObservacao = false;
      this.motivoAcao = "";
      this.acaoCallback = this.executarRejeicao;
      this.dialogConfirmacao = true;
    },

    async executarRejeicao() {
      if (!this.motivoAcao) {
        toast.warning("Motivo é obrigatório");
        return;
      }

      this.executandoAcao = true;
      try {
        const response = await transferenciaEstoqueService.rejeitarTransferencia(
          this.transferenciaSelecionada.id,
          { motivo_rejeicao: this.motivoAcao }
        );

        if (response?.success) {
          toast.success(response.message || "Transferência rejeitada com sucesso");
          this.dialogConfirmacao = false;
          this.fetchData();
        } else {
          toast.error(response?.message || "Erro ao rejeitar transferência");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao rejeitar transferência");
      } finally {
        this.executandoAcao = false;
      }
    },

    concluirTransferencia(item) {
      this.transferenciaSelecionada = item;
      this.tituloConfirmacao = "Concluir Transferência";
      this.mensagemConfirmacao = `Deseja concluir a transferência #${item.id}? Esta ação irá movimentar o estoque.`;
      this.corBotaoConfirmacao = "success";
      this.requererMotivo = false;
      this.requererObservacao = true;
      this.observacaoAcao = "";
      this.acaoCallback = this.executarConclusao;
      this.dialogConfirmacao = true;
    },

    async executarConclusao() {
      this.executandoAcao = true;
      try {
        const response = await transferenciaEstoqueService.concluirTransferencia(
          this.transferenciaSelecionada.id,
          { observacao_conclusao: this.observacaoAcao }
        );

        if (response?.success) {
          toast.success(response.message || "Transferência concluída com sucesso");
          this.dialogConfirmacao = false;
          this.fetchData();
        } else {
          toast.error(response?.message || "Erro ao concluir transferência");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao concluir transferência");
      } finally {
        this.executandoAcao = false;
      }
    },

    cancelarTransferencia(item) {
      this.transferenciaSelecionada = item;
      this.tituloConfirmacao = "Cancelar Transferência";
      this.mensagemConfirmacao = `Deseja cancelar a transferência #${item.id}?`;
      this.corBotaoConfirmacao = "warning";
      this.requererMotivo = true;
      this.requererObservacao = false;
      this.motivoAcao = "";
      this.acaoCallback = this.executarCancelamento;
      this.dialogConfirmacao = true;
    },

    async executarCancelamento() {
      if (!this.motivoAcao) {
        toast.warning("Motivo é obrigatório");
        return;
      }

      this.executandoAcao = true;
      try {
        const response = await transferenciaEstoqueService.cancelarTransferencia(
          this.transferenciaSelecionada.id,
          { motivo_cancelamento: this.motivoAcao }
        );

        if (response?.success) {
          toast.success(response.message || "Transferência cancelada com sucesso");
          this.dialogConfirmacao = false;
          this.fetchData();
        } else {
          toast.error(response?.message || "Erro ao cancelar transferência");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao cancelar transferência");
      } finally {
        this.executandoAcao = false;
      }
    },

    excluirTransferencia(item) {
      this.transferenciaSelecionada = item;
      this.tituloConfirmacao = "Excluir Transferência";
      this.mensagemConfirmacao = `Deseja excluir permanentemente a transferência #${item.id}?`;
      this.corBotaoConfirmacao = "error";
      this.requererMotivo = false;
      this.requererObservacao = false;
      this.acaoCallback = this.executarExclusao;
      this.dialogConfirmacao = true;
    },

    async executarExclusao() {
      this.executandoAcao = true;
      try {
        const response = await transferenciaEstoqueService.excluirTransferencia(
          this.transferenciaSelecionada.id
        );

        if (response?.success) {
          toast.success(response.message || "Transferência excluída com sucesso");
          this.dialogConfirmacao = false;
          this.fetchData();
        } else {
          toast.error(response?.message || "Erro ao excluir transferência");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao excluir transferência");
      } finally {
        this.executandoAcao = false;
      }
    },

    async verHistorico(item) {
      this.transferenciaSelecionada = item;
      this.dialogHistorico = true;
      this.loadingHistorico = true;

      try {
        const response = await transferenciaEstoqueService.buscarHistorico(item.id);
        if (response?.success) {
          this.historico = Array.isArray(response.dados) ? response.dados : [];
        } else {
          this.historico = [];
          toast.error(response?.message || "Erro ao carregar histórico");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar histórico");
        this.historico = [];
      } finally {
        this.loadingHistorico = false;
      }
    },

    confirmarAcao() {
      if (this.acaoCallback) {
        this.acaoCallback();
      }
    },

    getStatusColor(status) {
      const colors = {
        PENDENTE: "orange",
        PROCESSANDO: "blue",
        CONCLUIDO: "green",
        ERRO: "red",
        CANCELADO: "grey",
      };
      return colors[status] || "grey";
    },

    formatarNumero(valor) {
      if (!valor) return "0";
      return parseFloat(valor).toFixed(2);
    },

    verErro(item) {
      if (item.mensagem_erro) {
        toast.error(item.mensagem_erro);
      } else {
        toast.info("Nenhuma mensagem de erro disponível");
      }
    },

    formatarData(data) {
      if (!data) return "-";
      const date = new Date(data);
      return date.toLocaleDateString("pt-BR");
    },

    formatarDataHora(data) {
      if (!data) return "-";
      const date = new Date(data);
      return date.toLocaleString("pt-BR");
    },

    // Buscar nome do depósito pelo ID
    getNomeDeposito(idDeposito) {
      if (!idDeposito) return '';
      const deposito = this.depositos.find(d => d.id_deposito === idDeposito);
      return deposito ? deposito.nome_deposito : '';
    },

    // Verificar se item pode ser selecionado (não pode se já tem arquivo gerado)
    itemSelecionavel(item) {
      return !item.arquivo_exportado || item.arquivo_exportado.trim() === '';
    },

    // Validar antes de gerar Excel
    validarEGerarExcel() {
      // Verificar se alguma transferência já tem arquivo gerado
      const comArquivo = this.transferenciasSelecionadas.filter(
        t => t.arquivo_exportado && t.arquivo_exportado.trim() !== ''
      );

      if (comArquivo.length > 0) {
        const arquivos = [...new Set(comArquivo.map(t => t.arquivo_exportado))].join(', ');
        toast.error(`Algumas transferências já possuem arquivo Excel gerado: ${arquivos}. Cancele o arquivo primeiro para gerar novamente.`);
        return;
      }

      this.gerarExcel();
    },

    async gerarExcel() {
      if (this.transferenciasSelecionadas.length === 0) {
        toast.warning("Selecione pelo menos uma transferência");
        return;
      }

      this.loadingExcel = true;
      try {
        const transferencias = this.transferenciasSelecionadas.map(t => t.id);
        const nomeArquivo = 'transferencias_' + new Date().toISOString().replace(/[:.]/g, '_').slice(0, -5) + '.xlsx';
        
        const response = await transferenciaEstoqueService.gerarExcel({ 
          transferencias,
          nome_arquivo: nomeArquivo 
        });

        if (response?.success) {
          // Exibir mensagem com link para o Painel SAP Downloads
          toast.success(response.message || "Excel gerado! Acesse o Painel SAP Downloads para baixar.", {
            timeout: 8000,
            onClick: () => {
              this.$router.push({ name: 'SapDownloads' });
            }
          });
          
          // Adicionar à lista de arquivos recém-gerados
          this.arquivosRecemGerados.push({
            nome: response.data?.nome_arquivo || nomeArquivo,
            timestamp: Date.now()
          });
          
          // Limpar arquivos antigos (mais de 5 minutos)
          this.limparArquivosAntigos();
          
          // Limpar seleção
          this.transferenciasSelecionadas = [];
          
          // Atualizar lista
          this.fetchData();
        } else {
          toast.error(response?.message || "Erro ao gerar Excel");
        }
      } catch (error) {
        console.error("Erro ao gerar Excel:", error);
        toast.error("Erro ao gerar Excel");
      } finally {
        this.loadingExcel = false;
      }
    },

    // Limpar arquivos recém-gerados com mais de 5 minutos
    limparArquivosAntigos() {
      const cincoMinutosAtras = Date.now() - (5 * 60 * 1000);
      this.arquivosRecemGerados = this.arquivosRecemGerados.filter(
        arquivo => arquivo.timestamp > cincoMinutosAtras
      );
    },

    // Verificar se arquivo foi recém-gerado (nos últimos 5 minutos)
    arquivoRecemGerado(item) {
      // Não chamar limparArquivosAntigos aqui pois causa loop infinito no template
      const cincoMinutosAtras = Date.now() - (5 * 60 * 1000);
      return this.arquivosRecemGerados.some(
        arquivo => arquivo.nome === item.arquivo_exportado && arquivo.timestamp > cincoMinutosAtras
      );
    },

    // Abrir dialog de arquivos Excel
    async abrirDialogArquivosExcel() {
      this.dialogArquivosExcel = true;
      await this.listarArquivosExcel();
    },

    // Listar arquivos Excel gerados
    async listarArquivosExcel() {
      this.loadingArquivosExcel = true;
      try {
        const response = await transferenciaEstoqueService.listarArquivosExcel();
        
        if (response?.success) {
          this.arquivosExcel = response.dados || [];
        } else {
          toast.error(response?.message || "Erro ao listar arquivos");
          this.arquivosExcel = [];
        }
      } catch (error) {
        console.error("Erro ao listar arquivos Excel:", error);
        toast.error("Erro ao listar arquivos Excel");
        this.arquivosExcel = [];
      } finally {
        this.loadingArquivosExcel = false;
      }
    },

    // Baixar arquivo Excel
    async baixarArquivoExcel(nomeArquivo) {
      try {
        const response = await transferenciaEstoqueService.baixarExcel(nomeArquivo);
        
        if (response?.success) {
          toast.success("Download iniciado!");
        } else {
          toast.error(response?.message || "Erro ao baixar arquivo");
        }
      } catch (error) {
        console.error("Erro ao baixar arquivo:", error);
        toast.error("Erro ao baixar arquivo Excel");
      }
    },

    // Confirmar cancelamento de arquivo
    confirmarCancelarArquivo(arquivo) {
      this.tituloConfirmacao = "Cancelar Arquivo Excel";
      this.mensagemConfirmacao = `Deseja realmente cancelar o arquivo "${arquivo.arquivo_exportado}"? As ${arquivo.quantidade_transferencias} transferências poderão ser exportadas novamente.`;
      this.corBotaoConfirmacao = "error";
      this.requererMotivo = false;
      this.requererObservacao = false;
      this.acaoCallback = async () => {
        await this.cancelarArquivoExcel(arquivo.arquivo_exportado);
      };
      this.dialogConfirmacao = true;
    },

    // Cancelar arquivo Excel
    async cancelarArquivoExcel(arquivoExportado) {
      try {
        const response = await transferenciaEstoqueService.cancelarArquivoExcel(arquivoExportado);
        
        if (response?.success) {
          toast.success(response.message || "Arquivo cancelado com sucesso!");
          
          // Limpar seleção atual
          this.transferenciasSelecionadas = [];
          
          // Atualizar lista de transferências PRIMEIRO (aguardar)
          await this.fetchData();
          
          // Atualizar lista de arquivos
          await this.listarArquivosExcel();
        } else {
          toast.error(response?.message || "Erro ao cancelar arquivo");
        }
      } catch (error) {
        console.error("Erro ao cancelar arquivo:", error);
        toast.error("Erro ao cancelar arquivo Excel");
      }
    },

    visualizarTransferencia(item) {
      this.transferenciaSelecionada = item;
      this.dialogVisualizar = true;
    },

    verErro(item) {
      this.transferenciaSelecionada = item;
      toast.error(item.mensagem_erro || "Erro desconhecido");
    },

    async excluirTransferencia(item) {
      this.transferenciaSelecionada = item;
      this.tituloConfirmacao = "Confirmar Exclusão";
      this.mensagemConfirmacao = `Deseja realmente excluir a transferência #${item.id}?`;
      this.corBotaoConfirmacao = "error";
      this.requererMotivo = false;
      this.requererObservacao = false;
      this.acaoCallback = async () => {
        try {
          const response = await transferenciaEstoqueService.excluirTransferencia(item.id);
          if (response?.success) {
            toast.success("Transferência excluída com sucesso");
            this.fetchData();
          } else {
            toast.error(response?.message || "Erro ao excluir transferência");
          }
        } catch (error) {
          console.error("Erro ao excluir:", error);
          toast.error("Erro ao excluir transferência");
        }
      };
      this.dialogConfirmacao = true;
    },

    async executarAcao() {
      if (this.acaoCallback) {
        await this.acaoCallback();
        this.dialogConfirmacao = false;
        this.acaoCallback = null;
      }
    },

    voltarMenu() {
      this.$router.push("/menu");
    },

    abrirPainelSapDownloads() {
      this.$router.push({ name: 'SapDownloads' });
    },
  },
};
</script>

<style scoped>
.transferencias-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  background: white;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.filters-card {
  border-radius: 12px;
  overflow: hidden;
}

.filters-header {
  background: linear-gradient(135deg, #d84315 0%, #bf360c 100%);
  color: white;
}

.filters-title {
  font-weight: 600;
  font-size: 1rem;
}

.table-card {
  border-radius: 12px;
  overflow: hidden;
}

.scrollable-table {
  border-radius: 12px;
}

.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
